/**
 * COACHDIA Pulse — Realtime Audio Chat helper (Iter273 P0)
 * ========================================================
 *
 * Client WebRTC pour la Briefing Room : navigateur ↔ OpenAI Realtime API.
 * Pattern direct (aucun agent backend en P0) — playbook Emergent validé.
 *
 * Flow :
 *   1. POST /api/pulse-briefing/realtime/session  → session éphémère + client_secret
 *   2. getUserMedia({audio:true})                 → capture mic
 *   3. RTCPeerConnection.createOffer()
 *   4. POST /api/pulse-briefing/realtime/negotiate (SDP) → SDP answer
 *   5. DataChannel "oai-events" → envoyer session.update (instructions, tools, voice)
 *   6. Écouter events : parle/écoute, transcription, tool_call → callbacks UI
 *
 * Events exposés vers l'UI :
 *   - onState(state)                  : 'idle' | 'listening' | 'speaking' | 'thinking'
 *   - onTranscript({role, text, final})
 *   - onToolCall(name, args, callId)  : l'UI décide comment répondre (scroll-sync,
 *                                        fetch backend pour get_detail, etc.)
 *   - onError(err)
 */

export class PulseBriefingAudioChat {
  constructor({ apiBase, token, onState, onTranscript, onToolCall, onError, onResponseIncomplete, onConnectionState, onPlaybackState }) {
    this.apiBase = apiBase.replace(/\/$/, '');
    this.token = token;
    this._onStateExternal = onState || (() => {});
    this.onTranscript = onTranscript || (() => {});
    this.onToolCall = onToolCall || (() => {});
    this.onError = onError || ((e) => console.error('[Briefing]', e));
    // Iter301 — Reflète pc.connectionState (new/connecting/connected/
    // disconnected/failed/closed) pour permettre à l'UI d'afficher
    // un point de santé temps-réel dans le cockpit.
    this.onConnectionState = onConnectionState || (() => {});
    // Iter291 — callback déclenché si response.done arrive avec status:
    // incomplete (limite tokens, content_filter, etc.). L'UI peut alors
    // proposer un bouton "Continuer la réponse".
    this.onResponseIncomplete = onResponseIncomplete || (() => {});
    // Iter320 — State machine ULTRA SIMPLE (3 états) :
    //   'idle'                  → bouton Approfondir actif
    //   'speaking'              → IA parle réellement
    //   'ended_waiting_reset'   → IA terminée, grâce 3s avant idle
    //
    // Aucun bouton d'arrêt en UI. Aucune Pause. Aucun Stop. Aucun
    // verrou complexe. Source unique de vérité : lifecycle Realtime
    // (response.created → response.audio.done → analyser silence ≥1500ms
    // → ended_waiting_reset → idle 3s plus tard).
    this.onPlaybackState = onPlaybackState || (() => {});
    this._playbackState = 'idle';
    this._remoteStream = null;
    // End-detector analyser : silence ≥1500ms continu APRÈS response.audio.done
    this._audioContext = null;
    this._audioAnalyser = null;
    this._audioRafId = null;
    this._audioBuffer = null;
    this._lastAudioActivityAt = 0;
    this._audioDoneReceived = false;
    this._endedWaitTimer = null;
    // Wrapper interne : pilote le mic en fonction de l'état
    this.onState = (s) => {
      this._currentState = s;
      this._applyMicGating();
      this._onStateExternal(s);
    };
    this.pc = null;
    this.dc = null;
    this.audioEl = null;
    this.localStream = null;
    this.sessionConfig = null;
    this._currentAssistantText = '';
    this._currentUserText = '';
    this._currentState = 'idle';
    // Iter289 — Mic FERMÉ par défaut. Source unique d'ouverture =
    // toggle utilisateur (enableUserMic). Aucune ouverture automatique.
    this._micEnabledExternally = false;
    // Iter289 — Verrou anti-boucle. Empêche l'envoi de plusieurs
    // response.create concurrents. Reset sur response.done / cancelled / error.
    this._responseInFlight = false;
    // Iter300 — Watchdog anti-zombie : si aucun event de progrès n'arrive
    // pendant 45s alors qu'une réponse est en vol, on libère le verrou pour
    // ne pas bloquer indéfiniment l'UI (typiquement quand l'autoplay audio
    // est bloqué par le navigateur ou que le réseau drop côté desktop).
    this._inFlightWatchdog = null;
    this._inFlightStartedAt = 0;
    // Iter301 — Timeout d'ouverture du datachannel. Si dc.onopen ne fire
    // pas en 10s (firewall, NAT symétrique, blocage corporate), on lève
    // une erreur explicite pour que l'UX puisse proposer un retry.
    this._dcOpenTimeout = null;
    // Iter391 — Auto-continue silencieux quand OpenAI tronque la réponse
    // au plafond hard de 4096 tokens. Compteur remis à 0 sur chaque
    // nouvelle question utilisateur (askQuestion / commitAndSend) et
    // PRÉSERVÉ à travers les chaînes continueResponse. Plafond strict
    // anti-boucle infinie : 3 relances max par question initiale.
    this._autoContinueCount = 0;
    this._autoContinueMax = 3;
    this._autoContinueTimer = null;
  }

  _setResponseInFlight(value, source = '') {
    this._responseInFlight = !!value;
    if (this._inFlightWatchdog) {
      clearTimeout(this._inFlightWatchdog);
      this._inFlightWatchdog = null;
    }
    if (value) {
      this._inFlightStartedAt = Date.now();
      this._inFlightWatchdog = setTimeout(() => {
        if (this._responseInFlight) {
          console.warn(
            '[briefing-watchdog] response in flight >45s without progress — auto-release verrou (source=%s)',
            source
          );
          this._responseInFlight = false;
          this._inFlightWatchdog = null;
          try { this.onState('idle'); } catch (err) { /* noop */ }
        }
      }, 45000);
    } else {
      this._inFlightStartedAt = 0;
    }
  }

  /**
   * Iter320 — Source de vérité unique des contrôles IA (3 états).
   * Idempotent. Aucun verrou — UX simplifiée sans Stop/Pause.
   */
  _setPlaybackState(s) {
    if (this._playbackState === s) return;
    this._playbackState = s;
    console.debug('[briefing-playback]', s);
    try { this.onPlaybackState(s); } catch (err) { /* noop */ }
  }

  /**
   * Iter318 — Initialise un AnalyserNode Web Audio API sur la MediaStream remote.
   * Détecte UNIQUEMENT la fin réelle de la parole IA (silence ≥1500ms continu)
   * APRÈS que response.audio.done ait été reçu. Cela évite tout clignotement
   * sur les micro-pauses naturelles de respiration.
   */
  _initEndDetector(stream) {
    try {
      this._teardownEndDetector();
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) {
        console.warn('[briefing-audio] AudioContext unavailable, end-detection fallback to lifecycle only');
        return;
      }
      this._audioContext = new Ctx();
      if (this._audioContext.state === 'suspended') {
        this._audioContext.resume().catch(() => { /* noop */ });
      }
      const source = this._audioContext.createMediaStreamSource(stream);
      this._audioAnalyser = this._audioContext.createAnalyser();
      this._audioAnalyser.fftSize = 512;
      this._audioAnalyser.smoothingTimeConstant = 0.5;
      source.connect(this._audioAnalyser);
      this._audioBuffer = new Uint8Array(this._audioAnalyser.frequencyBinCount);
      const SILENCE_THRESHOLD = 3;
      const SILENCE_TIMEOUT_MS = 1500;

      const tick = () => {
        if (!this._audioAnalyser) return;
        this._audioRafId = requestAnimationFrame(tick);

        // L'analyser n'a aucun effet sauf si on est en 'speaking' ET que
        // response.audio.done a été reçu. Cela élimine les transitions
        // sur micro-pauses naturelles (zéro clignotement).
        if (this._playbackState !== 'speaking') return;
        if (!this._audioDoneReceived) return;

        this._audioAnalyser.getByteTimeDomainData(this._audioBuffer);
        let max = 0;
        for (let i = 0; i < this._audioBuffer.length; i++) {
          const v = Math.abs(this._audioBuffer[i] - 128);
          if (v > max) max = v;
        }
        const now = Date.now();
        if (max > SILENCE_THRESHOLD) {
          this._lastAudioActivityAt = now;
        } else if (
          this._lastAudioActivityAt > 0 &&
          now - this._lastAudioActivityAt > SILENCE_TIMEOUT_MS
        ) {
          console.debug('[briefing-audio] end detected (silence ≥1500ms post audio.done)');
          this._enterEndedWaitingReset();
        }
      };
      this._audioRafId = requestAnimationFrame(tick);
    } catch (err) {
      console.warn('[briefing-audio] end-detector init failed', err);
    }
  }

  _teardownEndDetector() {
    if (this._audioRafId) {
      cancelAnimationFrame(this._audioRafId);
      this._audioRafId = null;
    }
    try { this._audioAnalyser?.disconnect(); } catch (err) { /* noop */ }
    this._audioAnalyser = null;
    if (this._audioContext) {
      try { this._audioContext.close(); } catch (err) { /* noop */ }
      this._audioContext = null;
    }
    this._audioBuffer = null;
  }

  /**
   * Iter319 — Bascule en 'ended_waiting_reset' (contrôle "Suspendre" encore
   * visible 3s) puis 'idle'. Idempotent. Cancellable si nouvelle réponse démarre.
   */
  _enterEndedWaitingReset() {
    if (this._endedWaitTimer) return;
    this._setPlaybackState('ended_waiting_reset');
    this._endedWaitTimer = setTimeout(() => {
      this._endedWaitTimer = null;
      if (this._responseInFlight) return;
      this._setPlaybackState('idle');
    }, 3000);
  }

  _cancelEndedWaitTimer() {
    if (this._endedWaitTimer) {
      clearTimeout(this._endedWaitTimer);
      this._endedWaitTimer = null;
    }
  }

  /**
   * Lance la session : récupère la session éphémère, établit WebRTC.
   * @param {object} sessionConfig  { instructions, tools, voice, model, temperature }
   */
  async start(sessionConfig) {
    this.sessionConfig = sessionConfig;
    // Iter301 — État explicit 'connecting' pendant tout le handshake WebRTC.
    // L'UI désactive les boutons et affiche un point de santé jaune.
    this.onState('connecting');
    try {
      // 1. Ephemeral session (client_secret, on l'ignore volontairement ici :
      //    le backend utilise la main key pour négocier). Le endpoint negotiate
      //    côté Emergent accepte directement un offer SDP sans client_secret.
      await fetch(`${this.apiBase}/api/pulse-briefing/realtime/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      // 2. Mic — Iter288 : passif STRICT par défaut. Ouverture exclusivement
      //    via le toggle utilisateur (enableUserMic). Aucune ouverture
      //    automatique par le LLM ou le VAD.
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false,
          channelCount: 1,
          sampleRate: 24000,
        },
      });
      // Force immédiatement à fermé (mode passif).
      this.localStream.getAudioTracks().forEach((t) => { t.enabled = false; });

      // 3. PeerConnection — Iter303 : config ICE chargée depuis le backend
      // (Metered TURN privé, credentials éphémères avec cache 45min côté
      // serveur). ZÉRO fallback : si le provider TURN est indisponible, on
      // remonte l'erreur immédiatement à l'utilisateur, pas de connexion
      // dégradée silencieuse.
      const iceResp = await fetch(
        `${this.apiBase}/api/pulse-briefing/realtime/ice-servers`,
        { signal: AbortSignal.timeout(4000) },
      );
      if (!iceResp.ok) {
        let detail = '';
        try {
          const errBody = await iceResp.json();
          detail = errBody?.detail || '';
        } catch (err) { /* ignore parse error */ }
        throw new Error(
          `Configuration TURN indisponible (HTTP ${iceResp.status}). ${detail || 'Réessayez dans quelques minutes.'}`
        );
      }
      const iceData = await iceResp.json();
      if (!Array.isArray(iceData?.ice_servers) || iceData.ice_servers.length === 0) {
        throw new Error('Configuration TURN vide. Contactez le support.');
      }
      console.info(
        '[briefing-ice] using server-provided ICE config, provider=%s, servers=%d',
        iceData.provider, iceData.ice_servers.length,
      );
      this.pc = new RTCPeerConnection({
        iceServers: iceData.ice_servers,
        iceTransportPolicy: iceData.ice_transport_policy || 'all',
      });

      // Iter301 — Logs diagnostic ICE / Connection state (zéro effet runtime,
      // utiles pour reproduire les bugs réseau côté support).
      this.pc.oniceconnectionstatechange = () => {
        console.debug('[briefing-ice]', this.pc?.iceConnectionState);
      };
      this.pc.onconnectionstatechange = () => {
        const s = this.pc?.connectionState;
        console.debug('[briefing-conn]', s);
        try { this.onConnectionState(s); } catch (err) { /* noop */ }
        if (s === 'failed' || s === 'disconnected' || s === 'closed') {
          // Si la connexion casse alors qu'on était listening/speaking,
          // on libère le verrou pour permettre un relancement propre.
          this._setResponseInFlight(false, `pc-${s}`);
        }
      };

      // Audio element pour la voix IA
      this.audioEl = document.createElement('audio');
      this.audioEl.autoplay = true;
      this.audioEl.playsInline = true;
      // Iter319 — Pas d'event listeners pause/play sur audioEl. Source de
      // vérité unique = lifecycle Realtime (response.created/done) + analyser
      // end-detector. Pas de Pause utilisateur (deprecated Iter319).
      // Iter300 — Sur desktop, certains navigateurs (Chrome strict, Safari)
      // bloquent l'autoplay même après gesture si la latence WebRTC est élevée.
      this.pc.ontrack = (ev) => {
        // Iter319 — Conserve MediaStream + initialise end-detector analyser.
        // L'attachement à audioEl se fait UNIQUEMENT ici (premier track WebRTC)
        // ET dans response.created (réattachement après Stop). Aucune autre voie.
        this._remoteStream = ev.streams[0];
        this.audioEl.srcObject = this._remoteStream;
        this._initEndDetector(this._remoteStream);
        const p = this.audioEl.play();
        if (p && typeof p.catch === 'function') {
          p.catch((err) => {
            console.warn('[briefing-audio] autoplay blocked, retrying after gesture', err);
          });
        }
      };

      // Ajouter notre audio local
      this.localStream.getTracks().forEach((track) => {
        this.pc.addTrack(track, this.localStream);
      });

      // DataChannel pour events
      this.dc = this.pc.createDataChannel('oai-events');
      this.dc.onopen = () => this._onDataChannelOpen();
      this.dc.onmessage = (ev) => this._handleEvent(ev);

      // 4. SDP offer → backend negotiate
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);

      const answerResp = await fetch(`${this.apiBase}/api/pulse-briefing/realtime/negotiate`, {
        method: 'POST',
        body: offer.sdp,
        headers: { 'Content-Type': 'application/sdp' },
      });
      // Iter388 — Diagnostic premium : si la négociation échoue, on extrait
      // le vrai message d'erreur du backend (detail FastAPI ou JSON) pour
      // remonter une cause actionable côté UI ("clé OpenAI expirée", "quota
      // dépassé", "modèle indisponible"…) plutôt qu'un opaque "HTTP 502".
      if (!answerResp.ok) {
        let detail = '';
        try {
          const errBody = await answerResp.json();
          detail = errBody?.detail || errBody?.message || '';
        } catch (err) {
          try { detail = await answerResp.text(); } catch (err2) { /* noop */ }
        }
        throw new Error(
          `Salle de présentation indisponible (HTTP ${answerResp.status}). ${detail || 'Réessayez dans quelques minutes.'}`
        );
      }
      let answerJson = null;
      try {
        answerJson = await answerResp.json();
      } catch (err) {
        throw new Error('Réponse de négociation illisible. Réessayez ou contactez le support.');
      }
      const answerSdp = answerJson?.sdp;
      // Iter388 — Garde-fou défensif côté client : tout SDP valide commence
      // par "v=0" (RFC 4566). Si le backend renvoie autre chose (bug,
      // payload corrompu, downgrade emergentintegrations), on évite
      // l'exception cryptique de setRemoteDescription et on remonte un
      // message clair à l'utilisateur.
      if (typeof answerSdp !== 'string' || !answerSdp.trim() || !answerSdp.trimStart().startsWith('v=0')) {
        const preview = typeof answerSdp === 'string' ? answerSdp.slice(0, 120) : String(answerSdp);
        throw new Error(`Réponse SDP invalide reçue du serveur (${preview || 'payload vide'}).`);
      }
      await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

      // Iter301 — N'EST PLUS appelé ici. L'état passe à 'listening' uniquement
      // quand le datachannel est réellement ouvert (voir _onDataChannelOpen).
      // Si dc n'arrive jamais à 'open' (firewall / NAT bloque WebRTC), un
      // timeout 10s déclenche une erreur explicite.
      this._dcOpenTimeout = setTimeout(() => {
        if (!this.dc || this.dc.readyState !== 'open') {
          console.error('[briefing-conn] dc.onopen never fired after 10s — likely firewall/NAT blocking WebRTC');
          this.onState('idle');
          this.onError(new Error(
            'Connexion temps réel impossible. Votre réseau bloque WebRTC ' +
            '(firewall, proxy, NAT). Réessayez ou changez de réseau.'
          ));
          try { this.pc?.close(); } catch (err) { /* noop */ }
        }
      }, 10000);

      // Iter285 — Watchdog L3 SUPPRIMÉ. Aucun timer concurrent.
      // Le pipeline atomique narrate_page → response.done → onNarrationDone
      // est l'unique source de progression.
    } catch (e) {
      this.onState('idle');
      this.onError(e);
      throw e;
    }
  }

  /**
   * Envoi du session.update après ouverture du DataChannel.
   * Iter288 — VAD serveur DÉSACTIVÉ tant que l'utilisateur n'a pas activé
   * son toggle micro. Aucune écoute libre.
   */
  _onDataChannelOpen() {
    // Iter301 — Annule le timeout d'ouverture (succès) et passe l'UI en
    // 'listening' SEULEMENT maintenant. Avant cela, l'état restait à
    // 'connecting' → boutons Approfondir / Question / Mic désactivés.
    if (this._dcOpenTimeout) {
      clearTimeout(this._dcOpenTimeout);
      this._dcOpenTimeout = null;
    }
    const cfg = this.sessionConfig || {};
    // Iter390 — Migration session.update Beta → GA.
    //
    // Schéma GA (cf. OpenAI Realtime API GA docs + Azure migration guide) :
    //   session.type         : 'realtime' (REQUIS — cause de l'erreur
    //                          "Missing required parameter: 'session.type'")
    //   session.output_modalities (remplace l'ancien `modalities`)
    //   session.audio.input.transcription (remplace `input_audio_transcription`)
    //   session.audio.output.voice (remplace `voice` à la racine)
    //
    // Contrainte GA : on ne peut PAS demander 'audio' et 'text' en
    // simultané dans output_modalities. On garde 'audio' (briefing vocal) ;
    // le texte est disponible via response.audio_transcript.* (events
    // déjà gérés dans onDataChannelMessage).
    const payload = {
      type: 'session.update',
      session: {
        type: 'realtime',
        instructions: cfg.instructions || '',
        output_modalities: ['audio'],
        audio: {
          input: {
            // Iter390 v5 — Migration `whisper-1` (Beta) → `gpt-4o-mini-transcribe`
            // (GA). whisper-1 n'émettait plus d'events
            // `conversation.item.input_audio_transcription.completed`
            // → la transcription utilisateur restait invisible côté UI.
            // `gpt-4o-mini-transcribe` est le modèle GA stable et économique
            // pour la transcription temps réel input.
            transcription: { model: 'gpt-4o-mini-transcribe', language: 'fr' },
            // Iter390 v3 — GA : `turn_detection` migré sous `audio.input`
            // (au lieu de la racine session, qui renvoyait
            // "Unknown parameter: 'session.turn_detection'").
            turn_detection: cfg.turn_detection === undefined ? null : cfg.turn_detection,
          },
          output: {
            voice: cfg.voice || 'cedar',
          },
        },
        tools: cfg.tools || [],
        tool_choice: 'auto',
      },
    };
    this._send(payload);
    this.onState('listening');
    // Iter289 — AUCUN response.create initial. L'IA ne parle JAMAIS sans
    // question utilisateur. Voir askQuestion(text) pour déclencher
    // explicitement une réponse.
  }

  /**
   * Iter289 — askQuestion : seule voie de déclenchement d'une réponse IA.
   * Appelée par :
   *   • le toggle micro utilisateur (via VAD : voir enableUserMic + create_response:true)
   *   • le bouton "Poser une question" (saisie texte)
   *   • le bouton "Approfondir" (question pré-formatée)
   *
   * Pour une question textuelle, on injecte un conversation.item de type
   * input_text puis on déclenche response.create avec verrou anti-boucle.
   *
   * Retourne un objet : { ok:true } ou { ok:false, reason:'empty'|'in_flight'|'dc_closed' }.
   * Un appelant legacy peut continuer à traiter le retour comme booléen
   * (truthy ⇄ ok).
   */
  askQuestion(text, opts = {}) {
    const trimmed = String(text || '').trim();
    if (!trimmed) return { ok: false, reason: 'empty' };
    if (this._responseInFlight) {
      console.warn('[briefing] askQuestion skipped — response in flight');
      return { ok: false, reason: 'in_flight' };
    }
    if (!this.dc || this.dc.readyState !== 'open') {
      console.warn('[briefing] askQuestion skipped — datachannel not open');
      return { ok: false, reason: 'dc_closed' };
    }
    const sectionHint = opts.section ? ` [Section actuelle : ${opts.section}]` : '';
    this._send({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text: trimmed + sectionHint }],
      },
    });
    this._setResponseInFlight(true, 'askQuestion');
    // Iter391 — Reset compteur auto-continue : nouvelle question = nouvelle chaîne.
    this._autoContinueCount = 0;
    // Iter391 — max_output_tokens explicite à 4096 (plafond hard GA). Permet
    // de détecter sans ambiguïté la troncature via response.done.
    this._send({
      type: 'response.create',
      response: {
        // Iter390 — GA : `modalities` → `output_modalities`. Contrainte
        // GA : on ne peut pas demander audio + text simultanément. On
        // garde audio (briefing vocal) ; le texte est livré via les
        // events response.audio_transcript.* déjà gérés.
        output_modalities: ['audio'],
        max_output_tokens: 4096,
      },
    });
    return { ok: true };
  }

  /**
   * Iter291 — commitAndSend : seule méthode d'envoi vocal.
   * Flux strict :
   *   1. input_audio_buffer.commit  → force OpenAI à transcrire
   *      le buffer audio capturé jusqu'ici (zéro perte audio).
   *   2. session.update {turn_detection: null} → désactive le VAD pour
   *      éviter une 2ème création de response automatique.
   *   3. localStream tracks.enabled = false → coupe le micro proprement.
   *   4. response.create {modalities:['audio','text']} → demande la
   *      réponse complète, sans cap de tokens.
   *
   * Garde-fous :
   *   • Si datachannel pas ouvert → return false (l'UI affiche un toast).
   *   • Si _responseInFlight → on ne tue pas l'état audio mais on
   *     n'envoie pas un 2ème response.create concurrent.
   *   • Le verrou _responseInFlight passe à true ici pour bloquer
   *     toute nouvelle question tant que l'IA n'a pas terminé.
   */
  commitAndSend() {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.warn('[briefing] commitAndSend skipped — datachannel not open');
      return false;
    }
    // 1. Commit du buffer audio (transcription forcée, zéro perte)
    this._send({ type: 'input_audio_buffer.commit' });
    // 2. Désactiver le VAD pour éviter création concurrente.
    //    Iter390 v3 — GA : turn_detection sous audio.input.
    this._send({
      type: 'session.update',
      session: { type: 'realtime', audio: { input: { turn_detection: null } } },
    });
    this._micEnabledExternally = false;
    // 3. Couper la track audio (le micro est physiquement fermé)
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((t) => { t.enabled = false; });
    }
    // 4. Demander la réponse IA complète, sans cap
    if (!this._responseInFlight) {
      this._setResponseInFlight(true, 'commitAndSend');
      // Iter391 — Reset compteur auto-continue : nouvelle question = nouvelle chaîne.
      this._autoContinueCount = 0;
      this._send({
        type: 'response.create',
        response: { output_modalities: ['audio'], max_output_tokens: 4096 },
      });
    }
    console.debug('[briefing-mic] COMMIT & SEND — buffer committed, response.create dispatched');
    return true;
  }

  /**
   * Iter291 — enableUserMic : ouvre le micro SANS auto-create-response.
   * Le VAD reste actif uniquement pour générer les events
   * speech_started / speech_stopped (utiles à la waveform UI), mais
   * `create_response: false` garantit que SEUL `commitAndSend()` peut
   * déclencher une réponse IA. Zéro création concurrente.
   */
  enableUserMic() {
    if (this._responseInFlight) {
      this._send({ type: 'response.cancel' });
      this._setResponseInFlight(false, 'enableUserMic-cancel');
    }
    this._send({
      type: 'session.update',
      session: {
        // Iter390 v3 — GA : turn_detection sous audio.input.
        type: 'realtime',
        audio: {
          input: {
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 1500,
              create_response: false,
              interrupt_response: false,
            },
          },
        },
      },
    });
    this._micEnabledExternally = true;
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((t) => { t.enabled = true; });
    }
    console.debug('[briefing-mic] USER OPEN — VAD detect-only (no auto response), track enabled');
  }

  disableUserMic() {
    // Iter390 v3 — GA : turn_detection sous audio.input.
    this._send({
      type: 'session.update',
      session: { type: 'realtime', audio: { input: { turn_detection: null } } },
    });
    // Iter291 — Si on désactive le mic SANS commit, on jette explicitement
    // le buffer pour ne pas laisser de l'audio orphelin côté OpenAI.
    this._send({ type: 'input_audio_buffer.clear' });
    this._micEnabledExternally = false;
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((t) => { t.enabled = false; });
    }
    console.debug('[briefing-mic] USER CLOSE — VAD off, buffer cleared, track disabled');
  }

  /**
   * Iter289 — Mic gating STRICT.
   * Mic FERMÉ par défaut. Source unique d'ouverture = toggle utilisateur.
   */
  _applyMicGating() {
    if (!this.localStream) return;
    const tracks = this.localStream.getAudioTracks();
    if (!tracks.length) return;
    const wantMicOpen = this._micEnabledExternally === true;
    if (tracks[0].enabled !== wantMicOpen) {
      tracks.forEach((t) => { t.enabled = wantMicOpen; });
    }
  }

  /**
   * Iter291 — continueResponse : relance une response.create avec une
   * instruction explicite pour continuer la réponse précédente. Utilisé
   * par le bouton "Continuer la réponse" affiché quand response.done
   * arrive avec status: incomplete.
   */
  continueResponse() {
    if (!this.dc || this.dc.readyState !== 'open') return false;
    if (this._responseInFlight) return false;
    this._send({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text: 'Continue ta réponse précédente exactement là où elle s\'est arrêtée, sans répéter ce que tu as déjà dit.' }],
      },
    });
    this._setResponseInFlight(true, 'continueResponse');
    // Iter391 — IMPORTANT : on NE reset PAS _autoContinueCount ici.
    // Une chaîne d'auto-continue doit conserver son compteur cumulatif
    // pour respecter le plafond anti-boucle.
    this._send({
      type: 'response.create',
      response: { output_modalities: ['audio'], max_output_tokens: 4096 },
    });
    return true;
  }

  /**
   * Iter289 — Push-to-talk (alias) : ouverture mic immédiate.
   */
  cancelAndOpenMic() {
    this.enableUserMic();
  }

  // ===========================================================================
  // Iter320 — Méthodes Pause/Resume/Stop NEUTRALISÉES.
  //
  // Décision produit user (Iter320) : aucun bouton d'arrêt pendant la
  // narration IA. "Approfondir" devient simplement disabled pendant la
  // lecture, puis se réactive automatiquement à la fin réelle (3s post-fin
  // détectée par l'analyser audio + lifecycle Realtime).
  //
  // Ces méthodes sont conservées comme stubs pour compatibilité descendante
  // (au cas où un caller tiers les appellerait), mais NE FONT RIEN.
  // ===========================================================================

  pauseAssistantAudio() {
    console.warn('[briefing-tts] pauseAssistantAudio() is deprecated since Iter320 — UX simplified, no pause/stop buttons');
    return false;
  }

  resumeAssistantAudio() {
    console.warn('[briefing-tts] resumeAssistantAudio() is deprecated since Iter320 — UX simplified, no pause/stop buttons');
    return false;
  }

  stopAssistantAudio() {
    console.warn('[briefing-tts] stopAssistantAudio() is deprecated since Iter320 — UX simplified, no pause/stop buttons');
    return false;
  }

  /**
   * Annulation du mic en cours d'enregistrement : vide le buffer audio
   * côté OpenAI, désactive le VAD, et coupe la track locale. AUCUN
   * commit ni response.create n'est envoyé → aucun message dans la
   * transcription.
   */
  cancelMicCapture() {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.warn('[briefing-mic] cancelMicCapture skipped — datachannel not open');
      return false;
    }
    try {
      this._send({ type: 'input_audio_buffer.clear' });
      // Iter390 v3 — GA : turn_detection sous audio.input.
      this._send({
        type: 'session.update',
        session: { type: 'realtime', audio: { input: { turn_detection: null } } },
      });
    } catch (err) { /* noop */ }
    this._micEnabledExternally = false;
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((t) => { t.enabled = false; });
    }
    console.debug('[briefing-mic] CANCEL — buffer cleared, VAD off, track disabled, NO response triggered');
    return true;
  }

  /**
   * Getter public : vrai si une réponse IA est en cours de streaming.
   * Permet à l'UI de basculer le bouton Approfondir en contrôles TTS.
   */
  isResponseInFlight() {
    return this._responseInFlight === true;
  }

  /**
   * Getter public : état de pause de l'élément audio TTS.
   */
  isAudioPaused() {
    return !!(this.audioEl && this.audioEl.paused);
  }

  /**
   * Iter286 — `setOnNarrationDone` SUPPRIMÉ. Le pivot LLM-first n'utilise plus
   * de progression auto page-based. Le LLM pilote 100% des transitions via
   * focus_section / open_floor / close_floor / propose_decision.
   */

  /**
   * Iter285 — `setOnWatchdogStall` SUPPRIMÉ. La progression est désormais
   * pilotée uniquement par le pipeline atomique narrate_page → response.done
   * → onNarrationDone (filet unique, pas de timer concurrent).
   */

  /**
   * Iter291 — Détection robuste d'hallucinations Whisper.
   * Filtre uniquement les patterns d'hallucination AVÉRÉS (Amara,
   * sous-titres, abonnez-vous, musique symbols). Les phrases courtes
   * légitimes (« merci », « ok », « d'accord ») ne sont PLUS filtrées
   * — l'utilisateur peut poser une question courte sans qu'elle soit
   * jetée. Seul l'audio < 2 caractères reste rejeté.
   */
  _isWhisperHallucination(text) {
    if (!text) return true;
    const t = String(text).toLowerCase().trim();
    if (t.length < 2) return true;
    const patterns = [
      /amara\.org/,
      /sous[- ]?titres?\s+(réalisés?|faits?|générés?|par)/,
      /merci\s+d['’]avoir\s+regardé/,
      /thanks?\s+for\s+watching/,
      /abonn(ez|er)[\- ]vous/,
      /please\s+subscribe/,
      /[♪♫]/,
      /\[\s*(musique|music|silence|applaudissements|applause)\s*\]/,
      /\*\s*(music|musique|silence)\s*\*/,
    ];
    return patterns.some((p) => p.test(t));
  }

  _send(obj) {
    if (this.dc && this.dc.readyState === 'open') {
      this.dc.send(JSON.stringify(obj));
    }
  }

  /**
   * Iter289 — submitToolResult : retourne un résultat de tool au LLM.
   * Pour les tools qui DÉCLENCHENT une réponse subséquente
   * (get_section_context), on enchaîne response.create. Pour les tools
   * passifs (focus_section qui ne fait que scroller l'UI), on N'ENCHAÎNE
   * PAS de response.create : on attend la prochaine question utilisateur.
   */
  async submitToolResult(callId, resultText, opts = {}) {
    this._send({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: typeof resultText === 'string' ? resultText : JSON.stringify(resultText),
      },
    });
    if (opts.expectResponse !== false) {
      if (this._responseInFlight) {
        console.debug('[briefing] submitToolResult: response in flight, skip create');
      } else {
        this._setResponseInFlight(true, 'submitToolResult');
        this._send({ type: 'response.create' });
      }
    }
  }

  /**
   * Iter289 — sendUserText supprimé (remplacé par askQuestion qui pose
   * un verrou anti-boucle). sendInstruction supprimé (plus de relance auto).
   */

  /**
   * Handler principal des events OpenAI Realtime reçus sur le DataChannel.
   */
  _handleEvent(ev) {
    let data;
    try {
      data = JSON.parse(ev.data);
    } catch {
      return;
    }
    const t = data.type || '';

    switch (t) {
      case 'input_audio_buffer.speech_started':
        this.onState('listening_active');
        break;
      case 'input_audio_buffer.speech_stopped':
        this.onState('thinking');
        break;
      case 'response.created':
        this._setResponseInFlight(true, 'response.created');
        this._cancelEndedWaitTimer();
        this._audioDoneReceived = false;
        this._lastAudioActivityAt = 0;
        this.onState('speaking');
        try {
          if (this.audioEl) {
            if (!this.audioEl.srcObject && this._remoteStream) {
              this.audioEl.srcObject = this._remoteStream;
            }
            if (this.audioEl.paused) {
              this.audioEl.play().catch(() => { /* noop */ });
            }
          }
        } catch (err) { /* noop */ }
        this._setPlaybackState('speaking');
        break;
      case 'response.audio.delta':
      case 'response.output_audio.delta':  // Iter390 v4 — nom GA
        this.onState('speaking');
        break;
      case 'response.audio.done':
      case 'response.output_audio.done':  // Iter390 v4 — nom GA
        this._audioDoneReceived = true;
        if (this._lastAudioActivityAt === 0) {
          this._lastAudioActivityAt = Date.now();
        }
        // Filet de sécurité : si l'analyser n'est pas dispo, fallback timer 4s.
        if (!this._audioAnalyser) {
          setTimeout(() => {
            if (this._playbackState === 'speaking' && !this._responseInFlight) {
              this._enterEndedWaitingReset();
            }
          }, 4000);
        }
        break;
      case 'response.done': {
        const status = data?.response?.status || 'completed';
        const sd = data?.response?.status_details || {};
        const reason = String(sd.reason || sd.type || '').toLowerCase();

        // Iter391 — Détection ÉLARGIE de troncature. OpenAI GA peut renvoyer :
        //   status='incomplete' avec reason='max_output_tokens'         (cas idéal)
        //   status='completed'  mais status_details.reason contenant
        //                       'max', 'tokens', 'length' ou 'output'    (cas pernicieux)
        // On capture les deux pour ne JAMAIS laisser une réponse coupée
        // sans relance silencieuse.
        const truncationRegex = /max|tokens|length|output/i;
        const isTruncated = (
          status === 'incomplete'
          || (reason && truncationRegex.test(reason))
        );

        this._setResponseInFlight(false, t);
        this.onState('idle');
        if (!this._audioAnalyser && this._playbackState === 'speaking') {
          setTimeout(() => {
            if (this._playbackState === 'speaking' && !this._responseInFlight) {
              this._enterEndedWaitingReset();
            }
          }, 4000);
        }

        if (isTruncated) {
          if (this._autoContinueCount < this._autoContinueMax) {
            // Iter391 — Auto-continue silencieux. L'UI affiche un état
            // discret ("L'IA poursuit son analyse…"). L'utilisateur ne
            // voit AUCUN bouton manuel ; l'audio enchaîne naturellement.
            this._autoContinueCount += 1;
            console.debug(
              '[briefing-autocontinue] truncation detected (reason=%s, count=%d/%d) — relancing in 1500ms',
              reason || 'unknown', this._autoContinueCount, this._autoContinueMax
            );
            try {
              this.onResponseIncomplete(reason || 'max_tokens', {
                auto: true,
                attempt: this._autoContinueCount,
                max: this._autoContinueMax,
              });
            } catch (err) { /* noop */ }
            // Annule un éventuel timer en cours (sécurité)
            if (this._autoContinueTimer) clearTimeout(this._autoContinueTimer);
            this._autoContinueTimer = setTimeout(() => {
              this._autoContinueTimer = null;
              // Garde-fou : ne relance que si datachannel ouvert et pas
              // déjà en cours d'autre response.
              if (this.dc && this.dc.readyState === 'open' && !this._responseInFlight) {
                this.continueResponse();
              }
            }, 1500);
          } else {
            // Plafond auto-continue atteint → on bascule en mode manuel
            // (bouton "Continuer la réponse" visible).
            console.warn(
              '[briefing-autocontinue] max auto-continue reached (%d), falling back to manual mode',
              this._autoContinueMax
            );
            try {
              this.onResponseIncomplete(reason || 'max_tokens', {
                auto: false,
                attempt: this._autoContinueCount,
                max: this._autoContinueMax,
              });
            } catch (err) { console.error(err); }
          }
        } else if (this._autoContinueCount > 0) {
          // Iter391 — Fin propre d'une chaîne d'auto-continue. On notifie
          // l'UI que la réponse est désormais complète pour qu'elle puisse
          // retirer l'indicateur discret "L'IA poursuit son analyse…".
          try {
            this.onResponseIncomplete(null, { complete: true });
          } catch (err) { /* noop */ }
          this._autoContinueCount = 0;
        }
        break;
      }
      case 'response.cancelled':
        this._setResponseInFlight(false, 'response.cancelled');
        this.onState('idle');
        this._cancelEndedWaitTimer();
        this._setPlaybackState('idle');
        break;
      case 'response.audio_transcript.delta':
      case 'response.output_audio_transcript.delta':  // Iter390 v4 — nom GA
        this._currentAssistantText += data.delta || '';
        this.onTranscript({ role: 'assistant', text: this._currentAssistantText, final: false });
        break;
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done':  // Iter390 v4 — nom GA
        this.onTranscript({ role: 'assistant', text: data.transcript || this._currentAssistantText, final: true });
        this._currentAssistantText = '';
        break;
      case 'conversation.item.input_audio_transcription.completed': {
        const txt = data.transcript || '';
        if (this._isWhisperHallucination(txt)) {
          console.warn('[briefing-halluc] dropped:', JSON.stringify(txt));
          if (data.item_id) {
            this._send({ type: 'conversation.item.delete', item_id: data.item_id });
          }
          this._send({ type: 'response.cancel' });
          this._responseInFlight = false;
          break;
        }
        this.onTranscript({ role: 'user', text: txt, final: true });
        break;
      }
      case 'response.function_call_arguments.done': {
        let args = {};
        try { args = data.arguments ? JSON.parse(data.arguments) : {}; } catch { args = {}; }
        this.onToolCall(data.name, args, data.call_id);
        break;
      }
      case 'error': {
        const msg = (data?.error?.message || '').toLowerCase();
        const code = (data?.error?.code || '').toLowerCase();
        const benign = (
          code === 'conversation_already_has_active_response' ||
          code === 'response_cancel_not_active' ||
          code === 'item_not_found' ||
          msg.includes('already has an active') ||
          msg.includes('no active response') ||
          msg.includes('cancellation failed') ||
          msg.includes('item not found')
        );
        if (benign) {
          console.debug('[briefing-error-silent]', code || msg);
        } else {
          // Reset verrou en cas d'erreur réelle pour ne pas bloquer
          this._setResponseInFlight(false, 'realtime-error');
          this.onError(new Error(data?.error?.message || 'Realtime error'));
        }
        break;
      }
      default:
        // Iter390 v4 — Log défensif des events GA non gérés pour
        // surfacer immédiatement toute future migration de schéma OpenAI.
        // Filtre les events bruyants déjà connus (deltas audio bas niveau).
        if (t && !t.includes('rate_limits') && !t.startsWith('response.output_item') && !t.startsWith('response.content_part') && !t.startsWith('conversation.item.added') && !t.startsWith('conversation.item.done')) {
          console.debug('[briefing-event-unhandled]', t);
        }
        break;
    }
  }

  async stop() {
    if (this._dcOpenTimeout) {
      clearTimeout(this._dcOpenTimeout);
      this._dcOpenTimeout = null;
    }
    // Iter391 — Annule tout auto-continue en cours pour éviter qu'il
    // ne relance une response.create après la fermeture de la session.
    if (this._autoContinueTimer) {
      clearTimeout(this._autoContinueTimer);
      this._autoContinueTimer = null;
    }
    this._autoContinueCount = 0;
    // Iter320 — Cleanup state machine audio (3 états)
    this._cancelEndedWaitTimer();
    this._teardownEndDetector();
    try { this.dc && this.dc.close(); } catch (err) { /* noop */ }
    try { this.pc && this.pc.close(); } catch (err) { /* noop */ }
    try { this.localStream && this.localStream.getTracks().forEach((t) => t.stop()); } catch (err) { /* noop */ }
    try {
      if (this.audioEl) {
        this.audioEl.srcObject = null;
        this.audioEl.remove();
      }
    } catch (err) { /* noop */ }
    this.pc = null;
    this.dc = null;
    this.localStream = null;
    this.audioEl = null;
    this._remoteStream = null;
    this._audioDoneReceived = false;
    this._setResponseInFlight(false, 'stop');
    this._setPlaybackState('idle');
    this.onState('idle');
  }
}
